import React, { useEffect, useState, createElement } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Send, Eye, Loader2, Copy, Link as LinkIcon, HistoryIcon } from 'lucide-react';
import { BookOpenIcon, SearchIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { EmailPreview } from '../components/EmailPreview';
import { ConfirmModal } from '../components/ConfirmModal';
import { EmailBuilder } from '../components/builder/EmailBuilder';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { EmailBlock, EmailGlobalStyles, EmailBuilderSchema, DEFAULT_EMAIL_STYLES } from '../components/builder/types';
import { emailTemplatesApi, signupFormsApi, formConfigsApi, SignupFormRecord, emailTemplateLibraryApi, EmailLibraryRecord, landingPagesApi, LandingPageRecord, emailTemplateVersionsApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { uploadImageToHost } from '../utils/api';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
/** Clipboard copy with fallback for embedded contexts */
function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for non-HTTPS / embedded contexts
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  return new Promise((resolve, reject) => {
    document.execCommand('copy') ? resolve() : reject();
    textarea.remove();
  });
}
export function EmailTemplateEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // FIX: Team is always the workspace name, Region is the sub-region
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [showPreview, setShowPreview] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [emailBuilderBlocks, setEmailBuilderBlocks] = useState<EmailBlock[]>([]);
  const [emailBuilderStyles, setEmailBuilderStyles] = useState<EmailGlobalStyles>(DEFAULT_EMAIL_STYLES);
  // Form Linking State
  const [availableForms, setAvailableForms] = useState<SignupFormRecord[]>([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [linkedFormId, setLinkedFormId] = useState('');
  const [formFields, setFormFields] = useState<string[]>([]);
  const [isExtractingFields, setIsExtractingFields] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [templateData, setTemplateData] = useState({
    subject: '',
    preheader: '',
    html: '',
    confirmationUrl: ''
  });
  // Template picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatePickerTemplates, setTemplatePickerTemplates] = useState<EmailLibraryRecord[]>([]);
  const [isLoadingLibraryTemplates, setIsLoadingLibraryTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateApplied, setTemplateApplied] = useState(false);
  // Landing page picker state
  const [availableLandingPages, setAvailableLandingPages] = useState<LandingPageRecord[]>([]);
  const [isLoadingLandingPages, setIsLoadingLandingPages] = useState(false);
  // Image hosting state
  const [isHostingImages, setIsHostingImages] = useState(false);
  const [hostingProgress, setHostingProgress] = useState('');
  // Count base64 images in HTML
  const base64ImageCount = (templateData.html.match(/src=["']data:image\/[^"']+["']/gi) || []).length;
  // Host all embedded base64 images — upload each to server and replace with hosted URL
  const handleHostImages = async () => {
    const html = templateData.html;
    const base64Regex = /src=["'](data:image\/[^"']+)["']/gi;
    const matches: {
      full: string;
      dataUrl: string;
    }[] = [];
    let match;
    while ((match = base64Regex.exec(html)) !== null) {
      matches.push({
        full: match[0],
        dataUrl: match[1]
      });
    }
    if (matches.length === 0) {
      toast.info('No embedded base64 images found in the HTML');
      return;
    }
    setIsHostingImages(true);
    let updatedHtml = html;
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < matches.length; i++) {
      const {
        full,
        dataUrl
      } = matches[i];
      setHostingProgress(`Uploading image ${i + 1} of ${matches.length}...`);
      try {
        // Generate a filename from the data URL type
        const typeMatch = dataUrl.match(/data:image\/(\w+)/);
        const ext = typeMatch ? typeMatch[1].replace('jpeg', 'jpg') : 'png';
        const filename = `email-img-${Date.now()}-${i + 1}.${ext}`;
        const hostedUrl = await uploadImageToHost(dataUrl, filename);
        // Replace this specific occurrence
        const quote = full.includes('"') ? '"' : "'";
        updatedHtml = updatedHtml.replace(full, `src=${quote}${hostedUrl}${quote}`);
        successCount++;
      } catch (err) {
        failCount++;
        console.warn(`Failed to host image ${i + 1}:`, err);
      }
    }
    setTemplateData(prev => ({
      ...prev,
      html: updatedHtml
    }));
    setIsHostingImages(false);
    setHostingProgress('');
    if (successCount > 0 && failCount === 0) {
      const saved = html.length - updatedHtml.length;
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} hosted successfully`, {
        description: `Saved ~${saved.toLocaleString()} characters. Remember to click Update to save.`,
        duration: 5000
      });
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`${successCount} hosted, ${failCount} failed`, {
        description: 'Some images could not be uploaded. The hosting server may not be available yet.',
        duration: 5000
      });
    } else {
      toast.error('Failed to host images', {
        description: 'The hosting server may not be available. Set up chosen_image_upload.php on your server.',
        duration: 6000
      });
    }
  };
  // Load available forms on mount
  useEffect(() => {
    const loadForms = async () => {
      setIsLoadingForms(true);
      try {
        const forms = await signupFormsApi.list();
        const teamFilteredForms = forms.filter(f => f.Team === currentWorkspace.name || f.Team === team);
        setAvailableForms(teamFilteredForms);
      } catch (err) {
        console.error('Failed to load forms:', err);
        toast.error('Failed to load signup forms list');
      } finally {
        setIsLoadingForms(false);
      }
    };
    loadForms();
    // Load landing pages
    const loadLandingPages = async () => {
      setIsLoadingLandingPages(true);
      try {
        const pages = await landingPagesApi.list();
        const teamFiltered = pages.filter(p => (p.Team === currentWorkspace.name || p.Team === team) && p.Status?.toLowerCase() === 'active');
        setAvailableLandingPages(teamFiltered);
      } catch (err) {
        console.error('Failed to load landing pages:', err);
      } finally {
        setIsLoadingLandingPages(false);
      }
    };
    loadLandingPages();
  }, [currentWorkspace.name, team]);
  // Load existing template record when editing
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      emailTemplatesApi.get(id).then(record => {
        if (record) {
          // Handle Confirmation_Link — Creator URL fields may return
          // a string or an object { url: "...", title: "..." }
          let confirmUrl = '';
          const rawLink = (record as any).Confirmation_Link;
          if (rawLink) {
            if (typeof rawLink === 'string') {
              confirmUrl = rawLink;
            } else if (typeof rawLink === 'object' && rawLink.url) {
              confirmUrl = rawLink.url;
            }
          }
          setTemplateData({
            subject: record.Email_Subject || '',
            preheader: record.Preheader_Text || '',
            html: record.Email_HTML_Body || '',
            confirmationUrl: confirmUrl
          });
          // Handle Sign_Up_Form lookup — Creator may return:
          //   - a string ID like "12345"
          //   - an object like { ID: "12345", display_value: "Form Name" }
          //   - a display name string like "Form Name"
          const rawForm = (record as any).Sign_Up_Form;
          if (rawForm) {
            if (typeof rawForm === 'object' && rawForm.ID) {
              setLinkedFormId(String(rawForm.ID));
            } else if (typeof rawForm === 'string' && /^\d+$/.test(rawForm)) {
              setLinkedFormId(rawForm);
            } else if (typeof rawForm === 'number') {
              setLinkedFormId(String(rawForm));
            }
            // If it's a display name string, we can't resolve the ID here
            // — the user will need to re-select the form
          }
          // Restore builder state if available
          if ((record as any).Builder_Mode === 'builder' && (record as any).Builder_Schema) {
            try {
              const schema: EmailBuilderSchema = JSON.parse((record as any).Builder_Schema);
              setEmailBuilderBlocks(schema.blocks || []);
              setEmailBuilderStyles(schema.globalStyles || DEFAULT_EMAIL_STYLES);
              setEditorMode('builder');
            } catch {
              /* fallback to code mode */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/templates');
        }
      }).catch(err => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load template', {
          description: msg
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  // Extract fields when linked form changes
  useEffect(() => {
    if (!linkedFormId) {
      setFormFields([]);
      return;
    }
    const fetchAndExtract = async () => {
      setIsExtractingFields(true);
      try {
        // We need the full record to get the HTML
        const record = await signupFormsApi.get(linkedFormId);
        if (record && record.HTML) {
          const fields = extractFormFields(record.HTML);
          setFormFields(fields);
        } else {
          setFormFields([]);
        }
      } catch (err) {
        console.error('Failed to extract fields:', err);
        toast.error('Failed to extract form fields');
      } finally {
        setIsExtractingFields(false);
      }
    };
    fetchAndExtract();
  }, [linkedFormId]);
  // Template picker functions
  const openTemplatePicker = async () => {
    setShowTemplatePicker(true);
    setIsLoadingLibraryTemplates(true);
    try {
      const all = await emailTemplateLibraryApi.list();
      // Filter by team: show templates belonging to current workspace or with no team (shared)
      const teamFiltered = all.filter(t => !t.Team || t.Team === currentWorkspace.name.replace(/\s*Team\s*$/i, ''));
      setTemplatePickerTemplates(teamFiltered);
    } catch (err) {
      toast.error('Failed to load email templates library');
    } finally {
      setIsLoadingLibraryTemplates(false);
    }
  };
  const applyEmailTemplate = (tpl: EmailLibraryRecord) => {
    setTemplateData({
      subject: tpl.Email_Subject || '',
      preheader: tpl.Preheader_Text || '',
      html: tpl.Email_HTML_Body || '',
      confirmationUrl: ''
    });
    if (tpl.Builder_Mode === 'builder' && tpl.Builder_Schema) {
      try {
        const schema: EmailBuilderSchema = JSON.parse(tpl.Builder_Schema);
        setEmailBuilderBlocks(schema.blocks || []);
        setEmailBuilderStyles(schema.globalStyles || DEFAULT_EMAIL_STYLES);
        setEditorMode('builder');
      } catch {
        /* fallback to code */
      }
    }
    setTemplateApplied(true);
    setShowTemplatePicker(false);
    toast.success(`Template "${tpl.Template_Name}" applied`, {
      description: 'Customize and save when ready.',
      duration: 3000
    });
  };
  const filteredPickerTemplates = templatePickerTemplates.filter(t => {
    if (!templateSearch) return true;
    const q = templateSearch.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Email_Subject || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  const handleSave = async () => {
    if (!templateData.subject.trim()) {
      toast.error('Email Subject is required');
      return;
    }
    setIsSaving(true);
    // Build payload with correct Creator field API names
    const payload: Record<string, any> = {
      Email_Subject: templateData.subject,
      Preheader_Text: templateData.preheader,
      Email_HTML_Body: templateData.html,
      Team: team,
      Region: region,
      Builder_Mode: editorMode
    };
    // Save builder schema if in builder mode
    if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: emailBuilderBlocks,
        globalStyles: emailBuilderStyles
      });
    }
    // Confirmation Link (URL field — send as plain string)
    if (templateData.confirmationUrl) {
      payload.Confirmation_Link = templateData.confirmationUrl;
    }
    // Sign Up Form (lookup field — pass the ID as-is, never parseInt)
    if (linkedFormId) {
      payload.Sign_Up_Form = linkedFormId;
    }
    try {
      let savedTemplateId = id || '';
      if (isEditing && id) {
        await emailTemplatesApi.update(id, payload);
        savedTemplateId = id;
        toast.success('Email template updated', {
          description: `Template has been updated.`
        });
        (async () => {
          try {
            const versions = await emailTemplateVersionsApi.listByTemplateId(id);
            // Deactivate all existing versions
            for (const v of versions) {
              if (v.Is_Active === true) {
                try {
                  await emailTemplateVersionsApi.update(v.ID, {
                    Is_Active: false
                  });
                } catch (e) {
                  console.warn('Failed to deactivate version:', e);
                }
              }
            }
            const nextVersion = versions.length > 0 ? Math.max(...versions.map(v => v.Version_Number || 0)) + 1 : 1;
            await emailTemplateVersionsApi.create({
              Template_ID: id,
              Template_Name: templateData.subject,
              Version_Number: nextVersion,
              Email_HTML_Body: templateData.html,
              Email_Subject: templateData.subject,
              Preheader_Text: templateData.preheader,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Updated template',
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create email template version:', err);
          }
          auditService.log({
            actionType: 'updated',
            entityType: 'template',
            entityId: id,
            entityName: templateData.subject,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      } else {
        const result = await emailTemplatesApi.create(payload);
        // Extract the new record ID from Creator response
        if (result?.data?.ID) {
          savedTemplateId = String(result.data.ID);
        }
        toast.success('Email template created', {
          description: `Template has been saved to Zoho Creator.`
        });
        (async () => {
          try {
            await emailTemplateVersionsApi.create({
              Template_ID: savedTemplateId,
              Template_Name: templateData.subject,
              Version_Number: 1,
              Email_HTML_Body: templateData.html,
              Email_Subject: templateData.subject,
              Preheader_Text: templateData.preheader,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Initial version',
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create email template version:', err);
          }
          auditService.log({
            actionType: 'created',
            entityType: 'template',
            entityId: savedTemplateId,
            entityName: templateData.subject,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      }
      // Auto-create or update form configuration if a form is linked
      if (linkedFormId && savedTemplateId) {
        try {
          const result = await formConfigsApi.upsertByTemplate(savedTemplateId, linkedFormId, team);
          toast.success(result.action === 'updated' ? 'Form configuration updated' : 'Form configuration created', {
            description: 'Linked form and template in configurations.',
            duration: 3000
          });
        } catch (configErr) {
          // Don't block the template save — just warn
          console.error('Failed to sync form config:', configErr);
          toast.warning('Template saved, but form configuration sync failed', {
            description: 'You can manage it manually in Form Configurations.',
            duration: 5000
          });
        }
      }
      if (!isEditing) {
        navigate('/templates');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save email template', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleRestoreVersion = async (version: any) => {
    try {
      // 1. Update local state with version data
      setTemplateData({
        subject: version.Email_Subject || '',
        preheader: version.Preheader_Text || '',
        html: version.Email_HTML_Body || '',
        confirmationUrl: templateData.confirmationUrl // Preserve current link
      });
      // Try to parse Builder_Schema if it exists
      if (version.Builder_Schema) {
        try {
          const schema = JSON.parse(version.Builder_Schema);
          // Restore builder blocks and styles
          if (schema.blocks) setEmailBuilderBlocks(schema.blocks);
          if (schema.globalStyles) setEmailBuilderStyles(schema.globalStyles);
          // If there are blocks, switch to builder mode
          if (schema.blocks && schema.blocks.length > 0) {
            setEditorMode('builder');
          } else {
            setEditorMode('code');
          }
        } catch (e) {
          console.error('Failed to parse version Builder_Schema:', e);
          setEditorMode('code');
        }
      } else {
        // No schema, must be code mode
        setEditorMode('code');
      }
      // 2. Save the restored version's content to the main template record.
      // This ONLY updates the template record — it does NOT create or modify any version history entries.
      // All existing versions (v1, v2, v3, etc.) remain intact.
      // Determine the correct builder mode from the version data
      const restoredMode = version.Builder_Schema ? (() => {
        try {
          const s = JSON.parse(version.Builder_Schema);
          return s.blocks && s.blocks.length > 0 ? 'builder' : 'code';
        } catch {
          return 'code';
        }
      })() : 'code';
      const payload: Record<string, any> = {
        Email_Subject: version.Email_Subject || '',
        Preheader_Text: version.Preheader_Text || '',
        Email_HTML_Body: version.Email_HTML_Body || '',
        Builder_Schema: version.Builder_Schema || '',
        Builder_Mode: restoredMode
      };
      if (id) {
        await emailTemplatesApi.update(id, payload);
      }
    } catch (err) {
      console.error('Error restoring version:', err);
      throw err;
    }
  };
  const handleCopyVariable = (name: string) => {
    const variable = `{{${name}}}`;
    copyToClipboard(variable).then(() => {
      toast.success(`Copied ${variable}`, {
        description: 'Paste it into your HTML body',
        duration: 2000
      });
    }).catch(() => {
      toast.info(`Variable: ${variable}`, {
        description: 'Copy it manually and paste into your HTML',
        duration: 3000
      });
    });
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-3801">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3802" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-3803">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3804">
      <div className="flex items-center justify-between" data-id="element-3805">
        <div className="flex items-center gap-4" data-id="element-3806">
          <Link to="/templates" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-3807">
            <ArrowLeft className="h-5 w-5" data-id="element-3808" />
          </Link>
          <div data-id="element-3809">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3810">
              {isEditing ? 'Edit Template' : 'Create New Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-3811">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your email.' : 'Design your email with custom HTML.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3812">
          {/* Mode Toggle */}
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-3813">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3814">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && templateData.html && emailBuilderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3815">
              Builder
            </button>
          </div>

          {/* History Button - Only for saved templates */}
          {isEditing && id && <button onClick={() => setShowVersionHistory(true)} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors" title="Version History" data-id="element-3816">
              <HistoryIcon className="h-4 w-4" data-id="element-3817" />
            </button>}

          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3818">
            <Eye className="h-4 w-4" data-id="element-3819" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3820">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3821" /> : <Save className="h-4 w-4" data-id="element-3822" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Template Picker Banner */}
      {!isEditing && !templateApplied && !templateData.html && <div className="bg-white border border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-between" data-id="element-3823">
          <div className="flex items-center gap-3" data-id="element-3824">
            <div className="p-2 bg-amber-50 rounded-lg" data-id="element-3825">
              <BookOpenIcon className="h-5 w-5 text-amber-600" data-id="element-3826" />
            </div>
            <div data-id="element-3827">
              <p className="text-sm font-medium text-gray-900" data-id="element-3828">
                Start from a template
              </p>
              <p className="text-xs text-gray-500" data-id="element-3829">
                Pre-fill this email with a saved template from your library.
              </p>
            </div>
          </div>
          <button onClick={openTemplatePicker} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 whitespace-nowrap" data-id="element-3830">
            Browse Templates
          </button>
        </div>}

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-3831">
          {/* Subject + Preheader above builder */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-3832">
            <div className="grid grid-cols-2 gap-4" data-id="element-3833">
              <div data-id="element-3834">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3835">
                  Email Subject
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
              ...templateData,
              subject: e.target.value
            })} placeholder="Please confirm your subscription" data-id="element-3836" />
              </div>
              <div data-id="element-3837">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3838">
                  Preheader Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
              ...templateData,
              preheader: e.target.value
            })} placeholder="Brief summary..." data-id="element-3839" />
              </div>
            </div>
          </div>

          <EmailBuilder initialBlocks={emailBuilderBlocks} initialStyles={emailBuilderStyles} onSchemaChange={(blocks, globalStyles) => {
        setEmailBuilderBlocks(blocks);
        setEmailBuilderStyles(globalStyles);
      }} onHtmlChange={html => {
        setTemplateData(prev => ({
          ...prev,
          html
        }));
      }} mergeVariables={['confirmation_link', 'unsubscribe_link', 'team_name', ...formFields]} data-id="element-3840" />
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-3841">
          <div className="col-span-2 space-y-6" data-id="element-3842">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-id="element-3843">
              <div className="space-y-2" data-id="element-3844">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3845">
                  Email Subject
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
              ...templateData,
              subject: e.target.value
            })} placeholder="Please confirm your subscription" data-id="element-3846" />
              </div>
              <div className="space-y-2" data-id="element-3847">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3848">
                  Preheader Text (Optional)
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Brief summary displayed in inbox..." value={templateData.preheader} onChange={e => setTemplateData({
              ...templateData,
              preheader: e.target.value
            })} data-id="element-3849" />
              </div>
              <CodeEditor label="Email HTML Body" language="html" className="min-h-[400px]" charLimit={true} value={templateData.html} onChange={e => setTemplateData({
            ...templateData,
            html: e.target.value
          })} data-id="element-3850" />

              {/* Host Embedded Images action */}
              {base64ImageCount > 0 && <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-3851">
                  <div className="flex-1 min-w-0" data-id="element-3852">
                    <p className="text-sm font-medium text-amber-900" data-id="element-3853">
                      {base64ImageCount} embedded image
                      {base64ImageCount > 1 ? 's' : ''} detected
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-3854">
                      {isHostingImages ? hostingProgress : 'Base64 images bloat your HTML. Upload them to get short hosted URLs instead.'}
                    </p>
                  </div>
                  <button onClick={handleHostImages} disabled={isHostingImages} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-60 whitespace-nowrap transition-colors" data-id="element-3855">
                    {isHostingImages ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3856" /> : <LinkIcon className="h-4 w-4" data-id="element-3857" />}
                    {isHostingImages ? 'Hosting...' : 'Host Images'}
                  </button>
                </div>}
            </div>

            {/* GPT Code Assistant */}
            <GptCodeAssistant mode="email" currentCode={{
          html: templateData.html
        }} onCodeGenerated={code => {
          if (code.html) {
            setTemplateData(prev => ({
              ...prev,
              html: code.html
            }));
          }
        }} data-id="element-3858" />
          </div>

          <div className="space-y-6" data-id="element-3859">
            {/* Settings Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4" data-id="element-3860">
              <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-3861">
                Settings
              </h3>

              <div className="space-y-2" data-id="element-3862">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3863">
                  Team
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-3864" />
                <p className="text-xs text-gray-400" data-id="element-3865">
                  Automatically set from your current workspace
                </p>
              </div>

              <div className="space-y-2" data-id="element-3866">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3867">
                  Region
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={region || '(All regions)'} disabled data-id="element-3868" />
                <p className="text-xs text-gray-400" data-id="element-3869">
                  Automatically set from your current scope selection
                </p>
              </div>

              <div className="space-y-2" data-id="element-3870">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-2" data-id="element-3871">
                  Redirect After Confirmation
                </label>

                {/* Landing Page Quick Select */}
                {availableLandingPages.length > 0 && <div className="space-y-1.5" data-id="element-3872">
                    <label className="block text-xs font-medium text-gray-500" data-id="element-3873">
                      Select a landing page
                      {isLoadingLandingPages && <Loader2 className="inline h-3 w-3 animate-spin text-gray-400 ml-1" data-id="element-3874" />}
                    </label>
                    <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={availableLandingPages.some(p => templateData.confirmationUrl === `${window.location.origin}/page/${p.Url_Slug}`) ? templateData.confirmationUrl : ''} onChange={e => {
                if (e.target.value) {
                  setTemplateData({
                    ...templateData,
                    confirmationUrl: e.target.value
                  });
                }
              }} data-id="element-3875">
                      <option value="" data-id="element-3876">— Choose a landing page —</option>
                      {availableLandingPages.map(p => <option key={p.ID} value={`${window.location.origin}/page/${p.Url_Slug}`} data-id="element-3877">
                          {p.Page_Name} (/page/{p.Url_Slug})
                        </option>)}
                    </select>
                    <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-3878">
                      <span className="border-b border-gray-200 flex-1" data-id="element-3879" />
                      <span data-id="element-3880">or enter a custom URL</span>
                      <span className="border-b border-gray-200 flex-1" data-id="element-3881" />
                    </div>
                  </div>}

                <input type="url" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.confirmationUrl} onChange={e => setTemplateData({
              ...templateData,
              confirmationUrl: e.target.value
            })} placeholder="https://thechosen.tv/thank-you" data-id="element-3882" />
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-1.5" data-id="element-3883">
                  <p className="text-xs font-medium text-blue-800" data-id="element-3884">
                    How confirmation works:
                  </p>
                  <ol className="text-xs text-blue-700 list-decimal list-inside space-y-1" data-id="element-3885">
                    <li data-id="element-3886">
                      Subscriber clicks{' '}
                      <code className="bg-blue-100 px-1 rounded" data-id="element-3887">
                        {'{{confirmation_link}}'}
                      </code>{' '}
                      in the email
                    </li>
                    <li data-id="element-3888">
                      Their subscription is automatically marked as{' '}
                      <strong data-id="element-3889">Confirmed</strong>
                    </li>
                    <li data-id="element-3890">
                      They're redirected to the URL above (e.g., your landing
                      page or thank-you page)
                    </li>
                  </ol>
                  <p className="text-xs text-blue-600 mt-1.5" data-id="element-3891">
                    Leave empty to show a default confirmation success page
                    instead of redirecting.
                  </p>
                </div>
              </div>

              <div className="space-y-2" data-id="element-3892">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-2" data-id="element-3893">
                  Link to Signup Form
                  {isLoadingForms && <Loader2 className="h-3 w-3 animate-spin text-gray-400" data-id="element-3894" />}
                </label>
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={linkedFormId} onChange={e => setLinkedFormId(e.target.value)} data-id="element-3895">
                  <option value="" data-id="element-3896">Select a form...</option>
                  {availableForms.map(form => <option key={form.ID} value={form.ID} data-id="element-3897">
                      {form.Form_Name}
                    </option>)}
                </select>
                <p className="text-xs text-gray-500" data-id="element-3898">
                  Link a form to use its fields as variables in your email.
                </p>
              </div>
            </div>

            {/* Variables Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-6" data-id="element-3899">
              <div data-id="element-3900">
                <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-3901">
                  System Variables
                </h3>
                <p className="text-xs text-gray-500 mb-3" data-id="element-3902">
                  These are replaced automatically when a subscriber submits the
                  form. The final rendered email is saved on the submission
                  record.
                </p>
                <div className="space-y-2" data-id="element-3903">
                  <div onClick={() => handleCopyVariable('confirmation_link')} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors group" data-id="element-3904">
                    <code className="text-blue-600 font-medium" data-id="element-3905">
                      {'{{confirmation_link}}'}
                    </code>
                    <span className="text-xs text-gray-400 group-hover:text-gray-600" data-id="element-3906">
                      Required
                    </span>
                  </div>
                  <div onClick={() => handleCopyVariable('team_name')} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors group" data-id="element-3907">
                    <code className="text-gray-600 font-medium" data-id="element-3908">
                      {'{{team_name}}'}
                    </code>
                    <span className="text-xs text-gray-400 group-hover:text-gray-600" data-id="element-3909">
                      System
                    </span>
                  </div>
                </div>
              </div>

              <div data-id="element-3910">
                <div className="flex items-center justify-between mb-2" data-id="element-3911">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-3912">
                    Form Variables
                  </h3>
                  {isExtractingFields && <span className="text-xs text-blue-600 flex items-center gap-1" data-id="element-3913">
                      <Loader2 className="h-3 w-3 animate-spin" data-id="element-3914" />
                      Extracting...
                    </span>}
                </div>
                <p className="text-xs text-gray-500 mb-3" data-id="element-3915">
                  These match the field names in your signup form. Use them to
                  personalise the email (e.g., subscriber's name).
                </p>

                {!linkedFormId ? <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded border border-dashed border-gray-200" data-id="element-3916">
                    Link a signup form above to use form field variables.
                  </div> : formFields.length === 0 && !isExtractingFields ? <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded border border-dashed border-gray-200" data-id="element-3917">
                    No input fields found in the linked form's HTML.
                  </div> : <div className="flex flex-wrap gap-2" data-id="element-3918">
                    {formFields.map(field => <button key={field} onClick={() => handleCopyVariable(field)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors" title="Click to copy" data-id="element-3919">
                        <Copy className="h-3 w-3 opacity-50" data-id="element-3920" />
                        {field}
                      </button>)}
                  </div>}
              </div>

              {/* How it works explanation */}
              <div className="border-t border-gray-100 pt-4" data-id="element-3921">
                <h4 className="text-xs font-medium text-gray-700 mb-2" data-id="element-3922">
                  How variable replacement works
                </h4>
                <div className="bg-gray-50 rounded-md p-3 space-y-2 text-xs text-gray-600" data-id="element-3923">
                  <p data-id="element-3924">
                    <strong data-id="element-3925">1.</strong> You write your HTML with{' '}
                    <code className="bg-gray-200 px-1 rounded" data-id="element-3926">
                      {'{{variable}}'}
                    </code>{' '}
                    placeholders
                  </p>
                  <p data-id="element-3927">
                    <strong data-id="element-3928">2.</strong> When a subscriber submits the form, all
                    variables are replaced with their actual values
                  </p>
                  <p data-id="element-3929">
                    <strong data-id="element-3930">3.</strong> The final rendered HTML is saved on the
                    submission record as{' '}
                    <code className="bg-gray-200 px-1 rounded" data-id="element-3931">
                      Rendered_Email_HTML
                    </code>
                  </p>
                  <p data-id="element-3932">
                    <strong data-id="element-3933">4.</strong> Your Deluge script just reads{' '}
                    <code className="bg-gray-200 px-1 rounded" data-id="element-3934">
                      Rendered_Email_HTML
                    </code>{' '}
                    and sends it — no replacement needed in Deluge
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Template Picker Modal */}
      {showTemplatePicker && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3935">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3936">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowTemplatePicker(false)} data-id="element-3937" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" data-id="element-3938">
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-3939">
                <div className="flex items-center justify-between" data-id="element-3940">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-3941">
                    Choose an Email Template
                  </h3>
                  <button onClick={() => setShowTemplatePicker(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-id="element-3942">
                    &times;
                  </button>
                </div>
                <div className="mt-3 relative" data-id="element-3943">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-3944" />
                  <input type="text" placeholder="Search email templates..." value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-amber-500 focus:border-amber-500" autoFocus data-id="element-3945" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6" data-id="element-3946">
                {isLoadingLibraryTemplates ? <div className="flex items-center justify-center py-12" data-id="element-3947">
                    <Loader2 className="h-6 w-6 text-amber-500 animate-spin" data-id="element-3948" />
                    <span className="ml-2 text-sm text-gray-500" data-id="element-3949">
                      Loading templates...
                    </span>
                  </div> : filteredPickerTemplates.length === 0 ? <div className="text-center py-12" data-id="element-3950">
                    <p className="text-sm text-gray-500" data-id="element-3951">
                      {templateSearch ? 'No templates match your search.' : 'No email templates available yet.'}
                    </p>
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3952">
                    {filteredPickerTemplates.map(tpl => <button key={tpl.ID} onClick={() => applyEmailTemplate(tpl)} className="text-left p-4 border border-gray-200 rounded-lg hover:border-amber-300 hover:bg-amber-50/50 transition-colors group" data-id="element-3953">
                        <div className="flex items-start justify-between gap-2 mb-1" data-id="element-3954">
                          <p className="text-sm font-medium text-gray-900 group-hover:text-amber-900 truncate" data-id="element-3955">
                            {tpl.Template_Name || 'Untitled'}
                          </p>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${tpl.Builder_Mode === 'builder' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-3956">
                            {tpl.Builder_Mode === 'builder' ? 'Builder' : 'Code'}
                          </span>
                        </div>
                        {tpl.Email_Subject && <p className="text-xs text-gray-600 truncate mb-1" data-id="element-3957">
                            Subject: {tpl.Email_Subject}
                          </p>}
                        {tpl.Description && <p className="text-xs text-gray-500 line-clamp-2 mb-2" data-id="element-3958">
                            {tpl.Description}
                          </p>}
                        {tpl.Category && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600" data-id="element-3959">
                            {tpl.Category}
                          </span>}
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Template Preview" data-id="element-3960">
        <EmailPreview subject={templateData.subject} html={templateData.html} data-id="element-3961" />
      </PreviewModal>

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-3962" />

      {/* Version History Panel */}
      {isEditing && id && <VersionHistoryPanel isOpen={showVersionHistory} onClose={() => setShowVersionHistory(false)} entityType="email" entityId={id} entityName={templateData.subject || 'Untitled Template'} onRestore={handleRestoreVersion} data-id="element-3963" />}
    </div>;
}